import { IEquipmentTypes, VehicleEquipmentCategoryEnum } from 'next-common';
import React, { useEffect, useState } from 'react';
import { useDisableSkus, useEnableSkus, useSkusPriceUpdate, useUnselectSkus } from '../../../../../common';
import { calculateRetailPrice, createSkuListFromValue, eventEmitter, Events, prepareImageObject, priceFormatter } from '../../../../../utils';
import { IModelPackages } from '../../VehiclePickerPage';
import { IListverkChangeProps } from '../ResultGrid';
import { SwatchGrid } from '../ResultGrid.styled';
import filter from 'lodash/filter';
import { get } from 'lodash';
import { VehicleSelectableDescription } from './VehicleSelectableDescription';
import { v4 as uuidv4 } from 'uuid';
import { Card } from '@next-components/card';
import { CardType } from '@next-components/common-types';
import { MessagebarSeverityLevel } from '@next-components/messagebar';

export interface IVehicleListverk {
  data: IModelPackages;
  disabled: boolean;
  selectedListverk: IEquipmentTypes;
  onListverkChange: (data: IListverkChangeProps) => void;
  updatePrices: () => void;
}

export const VehicleListverk = (props: IVehicleListverk) => {
  const { data, disabled, selectedListverk, onListverkChange } = props;
  const { isDisableSKUonChange, isDisableSKUonLoad, disableSku, onloadDisableSku } = useDisableSkus();
  const { isEnableSKUonChange } = useEnableSkus();
  const { getUnselectSkuFlag } = useUnselectSkus();
  const priceUpdate = useSkusPriceUpdate();
  const [isPrepared, setIsPrepared] = useState<boolean>(false);
  const [active, setActive] = useState<boolean>(false);
  const [activeDesc, setActiveDesc] = useState<IEquipmentTypes>(null);

  useEffect(() => {
    eventEmitter.subscribe(Events.SKU_RULES_PREPARED, setIsPrepared);
    return () => {
      eventEmitter.unsubscribe(Events.SKU_RULES_PREPARED);
    };
  }, []);

  const onChangeListverk = (equipment: IEquipmentTypes) =>
    onListverkChange({
      equipment: selectedListverk?.id === equipment?.id ? null : equipment,
      price: getPrice(equipment),
      vatCode: equipment?.vatCode,
      isOptimisticUpdate: false,
      configType: VehicleEquipmentCategoryEnum.Listverk,
      dealerPriceExclVat: calculateRetailPrice(getPrice(equipment), equipment?.marginPercentage),
    });

  const isDisabled = (sku: string) => {
    const isDisable = isDisableSKUonChange(sku) ? true : isEnableSKUonChange(sku) ? false : isDisableSKUonLoad(sku);
    return isDisable;
  };

  const getPrice = (listverk: IEquipmentTypes) => {
    return priceUpdate?.has(listverk?.sku) ? Number(priceUpdate?.get(listverk?.sku)) : listverk?.priceExcludingVat;
  };

  useEffect(() => {
    //Update prices in cart && VAT Calculation
    if (selectedListverk?.id) {
      props.updatePrices();
    }
  }, [selectedListverk, priceUpdate]);

  const isDisableRuleApply = (equipment: IEquipmentTypes) => {
    const isDisable = isDisabled(equipment?.sku);
    const isUnselectedSku = getUnselectSkuFlag(equipment?.sku);
    if ((isDisable || isUnselectedSku) && isPrepared && selectedListverk?.id === equipment?.id) {
      onListverkChange({
        equipment: null,
        price: getPrice(equipment),
        vatCode: equipment?.vatCode,
        isOptimisticUpdate: true,
        configType: VehicleEquipmentCategoryEnum.Listverk,
        dealerPriceExclVat: calculateRetailPrice(getPrice(equipment), equipment?.marginPercentage),
      });
    }
    return isDisable;
  };
  const equipmentFilter: IEquipmentTypes[] = filter(data?.selectableEquipment ?? [], [
    'category',
    VehicleEquipmentCategoryEnum.Listverk,
  ]);
  useEffect(() => {
    const isDisable = isDisabled(selectedListverk?.sku);
    if (!isDisable && isPrepared && selectedListverk?.id) {
      if (selectedListverk) {
        onListverkChange({
          equipment: selectedListverk,
          price: getPrice(selectedListverk),
          vatCode: selectedListverk?.vatCode,
          isOptimisticUpdate: true,
          configType: VehicleEquipmentCategoryEnum.Listverk,
          dealerPriceExclVat: calculateRetailPrice(getPrice(selectedListverk), selectedListverk?.marginPercentage),
        });
      }
    }
  }, [selectedListverk, isDisabled(selectedListverk?.sku)]);

  const isDataAvailable = (equipment: IEquipmentTypes) => {
    return get(equipment, 'description');
  };

  const setCurrentData = (equipment: IEquipmentTypes) => {
    setActiveDesc(equipment);
    setActive(!active);
  };

  const getSkuNote = (sku: string) => {
    return createSkuListFromValue(disableSku, onloadDisableSku, sku);
  }
  
  return (
    <>
      <span className="label">Listverk</span>
      {equipmentFilter?.length === 0 ? (
        <p> Ingen Listverk tilgjengelig </p>
      ) : (
        <SwatchGrid className="vehicleListverk">
          {equipmentFilter?.map((equipment) => {
            const isSelected = selectedListverk?.id === equipment?.id;
            const customPriceTag = `${getPrice(equipment) > 0 ? '+' : ''}  ${priceFormatter(
              getPrice(equipment),
              equipment,
            )}`;

            return (
              <React.Fragment key={uuidv4()}>
                <Card
                  key={equipment?.id}
                  id={equipment?.id}
                  title={equipment?.name}
                  bosSwatch={prepareImageObject(equipment?.image?.[0])}
                  customPriceTag={customPriceTag}
                  type={CardType.BosCard}
                  selected={Boolean(isSelected)}
                  isDisabled={disabled || isDisableRuleApply(equipment)}
                  alertMessage={{
                    message: isDisabled(equipment?.sku) ? getSkuNote(equipment?.sku) : '',
                    typeOfAlert: MessagebarSeverityLevel.WARNING
                  }}
                  displayTag
                  updateSelected={() => onChangeListverk(equipment)}
                  disableTag={!isDataAvailable(equipment)}
                  tagValue={equipment?.sku}
                  onTagClick={() => setCurrentData(equipment)}
                />
              </React.Fragment>
            );
          })}
          <VehicleSelectableDescription
            data={activeDesc}
            active={active}
            onClose={() => {
              setActive(!active);
            }}></VehicleSelectableDescription>
        </SwatchGrid>
      )}
    </>
  );
};
